import { useEffect, useState } from 'react';
import { db, auth } from '../firebase-config';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import "../style/Profile.css"

function EditProfile() {
    const [userData, setUserData] = useState(null);
    const [preferredName, setPreferredName] = useState('');
    const [degree, setDegree] = useState('');
    const [bio, setBio] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhone] = useState('');
    const [isPreferredNameEnabled, setIsPreferredNameEnabled] = useState(false);
    const [isDegreeEnabled, setIsDegreeEnabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();
    const [userRole, setUserRole] = useState('Student'); // Default to Student
    const [profilePicUrl, setProfilePicUrl] = useState('');
    const [coverPhotoUrl, setCoverPhotoUrl] = useState('');
    const presetProfilePics = [
        "/Images/ProfilePicturePreset1.png",
        "/Images/ProfilePicturePreset2.png",
        "/Images/ProfilePicturePreset3.png",
        "/Images/ProfilePicturePreset4.png",
        "/Images/ProfilePicturePreset5.png",
        "/Images/ProfilePicturePreset6.png",
    ];
    const [showPresetModal, setShowPresetModal] = useState(false);
    const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');
    const coverPhotoPresets = [
        "/Images/CoverPicturePreset1.png",
        "/Images/CoverPicturePreset2.png",
        "/Images/CoverPicturePreset3.png",
    ];
    
    const [showCoverModal, setShowCoverModal] = useState(false);
    const [selectedCoverPreview, setSelectedCoverPreview] = useState('');
    

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const user = auth.currentUser;
                if (user) {
                    const userDoc = await getDoc(doc(db, "Users", user.uid));
                    if (userDoc.exists()) {
                        setUserData(userDoc.data());
                        setPreferredName(userDoc.data().preferredName || '');
                        setDegree(userDoc.data().degree || '');
                        setBio(userDoc.data().bio || '');
                        setEmail(userDoc.data().email || '');
                        setPhone(userDoc.data().phoneNumber || '');
                        setProfilePicUrl(userDoc.data().profilePicUrl || '');
                        setCoverPhotoUrl(userDoc.data().coverPhotoUrl || '');
                        setIsPreferredNameEnabled(userDoc.data().showPreferredName !== false); 
                        setIsDegreeEnabled(userDoc.data().showDegree !== false); // Default to true if not set
                        setUserRole(userDoc.data().role || 'Student'); // Default to Student if not specified
                    }
                }
            } catch (err) {
                console.error("Error fetching user data: ", err);
            }
        };
        fetchUserData();
    }, []);

    const handleSaveClick = async () => {
        const user = auth.currentUser;
        if (!user) {
            console.error("No user is signed in");
            return;
        }

        setIsSaving(true);

        try {
            const userRef = doc(db, "Users", user.uid);
            
            // Prepare the data to update
            const updatedData = {
                preferredName: preferredName === '' ? null : preferredName,  // Set to null if empty
                degree: degree === '' ? null : degree,  // Set to null if empty
                bio: bio,
                email: email,
                phoneNumber: phoneNumber,
                showPreferredName: preferredName === '' ? false : isPreferredNameEnabled, // Set to false if preferredName is empty
                showDegree: degree === '' ? false : isDegreeEnabled,  // Set to false if degree is empty
                lastUpdated: new Date() // Optional: track when the profile was last updated
            };

            // Update the document in Firestore
            await updateDoc(userRef, updatedData);
            
            alert('Profile updated successfully!');
            navigate('/profile');
        } catch (error) {
            console.error("Error updating profile: ", error);
            alert('Failed to update profile. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelClick = () => {
        navigate('/profile');
    };

    const handleProfilePicClick = () => {
        setSelectedPreviewUrl(profilePicUrl || ''); // show current
        setShowPresetModal(true);
    };
    
    const handleCoverPhotoClick = () => {
        setSelectedCoverPreview(coverPhotoUrl || '');
        setShowCoverModal(true);
    };

    const handleCoverSelect = (url) => {
        setSelectedCoverPreview(url);
    };
    
    const handleSaveCover = async () => {
        try {
            const userRef = doc(db, "Users", auth.currentUser.uid);
            await updateDoc(userRef, { coverPhotoUrl: selectedCoverPreview });
            setCoverPhotoUrl(selectedCoverPreview);
        } catch (err) {
            console.error("Failed to save selected cover photo:", err);
        }
        setShowCoverModal(false);
    };
    
    const handleRemoveCover = () => {
        setSelectedCoverPreview('');
    };
    
    const handleCancelCover = () => {
        setShowCoverModal(false);
        setSelectedCoverPreview('');
    };
    
    const handlePresetSelect = (url) => {
        setSelectedPreviewUrl(url);
    };
    
    const handleSavePreset = async () => {
        try {
            const userRef = doc(db, "Users", auth.currentUser.uid);
            await updateDoc(userRef, { profilePicUrl: selectedPreviewUrl });
            setProfilePicUrl(selectedPreviewUrl);
        } catch (err) {
            console.error("Failed to save selected picture:", err);
        }
        setShowPresetModal(false);
    };
    
    const handleRemoveImage = () => {
        setSelectedPreviewUrl('');
    };
    
    const handleCancelPreset = () => {
        setShowPresetModal(false);
        setSelectedPreviewUrl('');
    };

    return (
        <div className="edit-profile-container">
            {userData && (
                <div className="profile-content-wrapper">
                    <div className="profile-box">
                        {/* Cover Photo */}
                        <div className="cover-photo-container" onClick={handleCoverPhotoClick}>
                            {coverPhotoUrl ? (
                                <img
                                    src={coverPhotoUrl}
                                    alt="Cover"
                                    className="cover-image"
                                />
                            ) : null}
                            <div className="cover-photo-overlay">Edit Cover Photo</div>
                        </div>

                        {/* Cover Photo Modal (Presets Only) */}
                        {showCoverModal && (
                            <div className="modal-overlay">
                                <div className="preset-modal cover-modal">
                                    <h3 className="modal-title">Select a Cover Photo</h3>
                                    <div className="cover-preview-rectangle">
                                        {selectedCoverPreview ? (
                                            <img
                                                src={selectedCoverPreview}
                                                alt="Cover Preview"
                                                className="cover-preview-image"
                                            />
                                        ) : (
                                            <div className="cover-preview-placeholder">
                                                No Cover Selected
                                            </div>
                                        )}
                                    </div>
                                    <div className="preset-grid">
                                        {coverPhotoPresets.map((url, idx) => (
                                            <img
                                                key={idx}
                                                src={url}
                                                alt={`Cover Preset ${idx + 1}`}
                                                className={`preset-thumbnail ${selectedCoverPreview === url ? 'selected' : ''}`}
                                                onClick={() => handleCoverSelect(url)}
                                            />
                                        ))}
                                    </div>
                                    <div className="modal-buttons">
                                        <button onClick={handleSaveCover} className="modal-btn save">Save</button>
                                        <button onClick={handleRemoveCover} className="modal-btn remove">Remove Image</button>
                                        <button onClick={handleCancelCover} className="modal-btn cancel">Cancel</button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* White Section */}
                        <div className="white-section-container">
                            {/* Name and toggles section offset to the right of profile picture */}
                            <div className='name-section'>
                                <h2> {userData.firstName || ''} {userData.lastName || ''} </h2>

                                {/* Input Fields with Toggle */}
                                <div className="input-container">
                                    <div className="input-group">
                                        <div
                                            className={`toggle-button ${isPreferredNameEnabled ? 'on' : 'off'}`}
                                            onClick={() => setIsPreferredNameEnabled(!isPreferredNameEnabled)}
                                        >
                                            {isPreferredNameEnabled ? 'ON' : 'OFF'}
                                        </div>
                                        <input
                                            type="text"
                                            value={preferredName}
                                            onChange={(e) => setPreferredName(e.target.value)}
                                            placeholder="Preferred Name"
                                            className="input-field"                                     
                                        />
                                    </div>

                                    <div className="input-group">
                                        <div
                                            className={`toggle-button ${isDegreeEnabled ? 'on' : 'off'}`}
                                            onClick={() => setIsDegreeEnabled(!isDegreeEnabled)}
                                        >
                                            {isDegreeEnabled ? 'ON' : 'OFF'}
                                        </div>
                                        <input
                                            type="text"
                                            value={degree}
                                            onChange={(e) => setDegree(e.target.value)}
                                            placeholder={userRole === 'Teacher' ? "Teacher of [Degree]" : "Student Degree"}
                                            className="degree-input-field"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Bio, Email and Phone Section - Full width stretching under profile pic */}
                            <div className="under-profile-section">
                                {/* Bio with inline label - changed from textarea to input */}
                                <div className="field-container">
                                    <div className="label-style">Bio:</div>
                                    <input
                                        type="text"
                                        value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                        className="input-style"
                                    />
                                </div>

                                {/* Email with inline label */}
                                <div className="field-container">
                                    <div className="label-style">Email:</div>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="input-style"
                                    />
                                </div>

                                {/* Phone with inline label */}
                                <div className="field-container">
                                    <div className="label-style">Phone:</div>
                                    <input
                                        type="text"
                                        value={phoneNumber}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="input-style"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Save and Cancel Buttons */}
                        <div className="button-container">
                            {/* Save Button */}
                            <button 
                                className="save-button" 
                                onClick={handleSaveClick}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            {/* Cancel Button */}
                            <button 
                                className="cancel-button" 
                                onClick={handleCancelClick}                       
                            >
                                Cancel
                            </button>
                        </div>

                        {/* Profile Picture */}
                        <div
                            className={`profile-picture-container ${profilePicUrl ? 'has-image' : 'no-image'}`}
                            onClick={handleProfilePicClick}
                        >
                            {profilePicUrl ? (
                                <img src={profilePicUrl} alt="Profile" className="profile-picture-image" />
                            ) : (
                                <span className="initials">
                                    {userData.firstName?.charAt(0) || ''}{userData.lastName?.charAt(0) || ''}
                                </span>
                            )}
                            <div className="edit-label">Edit Picture</div>
                        </div>

                        {showPresetModal && (
                            <div className="modal-overlay">
                                <div className="preset-modal">
                                    <h3 className="modal-title">Select a Profile Picture</h3>
                                    <div className="modal-preview">
                                        {selectedPreviewUrl ? (
                                            <img src={selectedPreviewUrl} alt="Preview" className="preview-image" />
                                        ) : (
                                            <div className="initials preview-initials">
                                                {userData.firstName?.charAt(0) || ''}{userData.lastName?.charAt(0) || ''}
                                            </div>
                                        )}
                                    </div>
                                    <div className="preset-grid">
                                        {presetProfilePics.map((url, idx) => (
                                            <img
                                                key={idx}
                                                src={url}
                                                alt={`Preset ${idx + 1}`}
                                                className={`preset-thumbnail ${selectedPreviewUrl === url ? 'selected' : ''}`}
                                                onClick={() => handlePresetSelect(url)}
                                            />
                                        ))}
                                    </div>
                                    <div className="modal-buttons">
                                        <button onClick={handleSavePreset} className="modal-btn save">Save</button>
                                        <button onClick={handleRemoveImage} className="modal-btn remove">Remove Image</button>
                                        <button onClick={handleCancelPreset} className="modal-btn cancel">Cancel</button>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}
        </div>
    );
}

export default EditProfile;